import React, { useState, useEffect } from 'react';
import { useAudio } from '../context/AudioContext';
import { useDynamicTheme } from '../context/DynamicThemeContext';
import { Play, Pause, Volume2, VolumeX, History, Share2 } from 'lucide-react';
import { Slider } from './ui/slider';
import TrackHistory, { addToHistory, getHistory } from './TrackHistory';
import InstallPWA from './InstallPWA';
import ShareMenu from './ShareMenu';
import DonateButton from './DonateButton';
import './RadioPlayer.css';

const RadioPlayer = () => {
  const { isPlaying, volume, currentTrack, error, isLoading, play, pause, changeVolume } = useAudio();
  const { themeColors, displayArtwork } = useDynamicTheme();
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(0.7);
  const [showHistory, setShowHistory] = useState(false);
  const [historyCount, setHistoryCount] = useState(0);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Apply dynamic background
  useEffect(() => {
    document.body.style.background = themeColors.background;
    document.body.style.transition = 'background 1.5s ease-in-out';
  }, [themeColors.background]);

  // Update history count
  useEffect(() => {
    const updateCount = () => {
      const history = getHistory();
      setHistoryCount(history.length);
    };
    updateCount();
    const interval = setInterval(updateCount, 5000);
    return () => clearInterval(interval);
  }, []);

  // Add track to history when it changes (only while actually playing)
  useEffect(() => {
    if (isPlaying && currentTrack && currentTrack.title && currentTrack.title !== 'Live') {
      addToHistory({
        title: currentTrack.title,
        artist: currentTrack.artist,
        album: currentTrack.album,
        artwork: currentTrack.artwork
      });
      const history = getHistory();
      setHistoryCount(history.length);
    }
  }, [currentTrack.title, currentTrack.artist, currentTrack.artwork, isPlaying]);

  const togglePlay = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  const toggleMute = () => {
    if (isMuted) {
      changeVolume(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      changeVolume(0);
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (value) => {
    const newVolume = value[0];
    changeVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  const volumePercent = Math.round((isMuted ? 0 : volume) * 100);

  return (
    <div className="radio-player-container">
      {/* LIVE badge - pill outside the card */}
      <div className="live-badge" data-testid="live-badge">
        <span className="live-dot"></span>
        LIVE 24/7 <span className="live-sep">•</span> 320kbps
      </div>

      {/* Player Card */}
      <div className="player-card" data-testid="player-card">
        {/* Artwork */}
        <div className="artwork-container">
          <div className="artwork-wrapper">
            <img
              src={displayArtwork}
              alt="Album Artwork"
              className="artwork-image"
              data-testid="album-artwork"
            />
          </div>
        </div>

        {/* Track Info */}
        <div className="track-info">
          {isLoading ? (
            <>
              <h2 className="track-title">Connexion...</h2>
              <p className="track-artist">Chargement du stream</p>
            </>
          ) : (
            <>
              <div className="track-title-row">
                <h2 className="track-title" data-testid="track-title">{currentTrack.title}</h2>
                {currentTrack.title && currentTrack.title !== 'Live' && (
                  <ShareMenu
                    track={currentTrack}
                    anchor="center"
                    trigger={
                      <button className="share-track-btn" data-testid="share-track-button">
                        <Share2 size={18} />
                      </button>
                    }
                  />
                )}
              </div>
              <p className="track-artist" data-testid="track-artist">{currentTrack.artist}</p>
              {currentTrack.album && (
                <p className="track-album" data-testid="track-album">{currentTrack.album}</p>
              )}
              <p className="track-genre">Rock and roll • Rockabilly • Blues • Country • Jazz</p>
            </>
          )}
        </div>

        {/* Error Message */}
        {error && isPlaying && (
          <div className="error-message">{error}</div>
        )}

        {/* Controls: volume + play */}
        <div className="controls-row">
          <button
            onClick={toggleMute}
            onMouseEnter={() => setShowVolumeSlider(true)}
            className="volume-button"
            data-testid="mute-button"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || volume === 0 ? (
              <VolumeX size={20} />
            ) : (
              <Volume2 size={20} />
            )}
          </button>

          <button
            onClick={togglePlay}
            className={`play-button ${isLoading ? 'loading' : ''}`}
            data-testid="play-pause-button"
            aria-label={isPlaying ? 'Pause' : 'Play'}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : isPlaying ? (
              <Pause size={36} strokeWidth={2.5} fill="currentColor" />
            ) : (
              <Play size={36} strokeWidth={2.5} fill="currentColor" style={{ marginLeft: '4px' }} />
            )}
          </button>
        </div>

        {/* Volume slider - shown on hover/click of volume button */}
        <div
          className={`volume-slider-wrapper ${showVolumeSlider ? 'open' : ''}`}
          onMouseEnter={() => setShowVolumeSlider(true)}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          <Slider
            value={[isMuted ? 0 : volume]}
            onValueChange={handleVolumeChange}
            max={1}
            step={0.01}
            className="custom-slider"
            data-testid="volume-slider"
          />
          <span className="volume-percent">{volumePercent}%</span>
        </div>
      </div>

      {/* Footer */}
      <footer className="radio-footer" data-testid="radio-footer">
        <DonateButton />
        <p>© Majestic Jukebox Radio • Stream 320kbps</p>
        <a
          href="https://www.majesticjukeboxradio.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="footer-link"
          data-testid="footer-link"
        >
          MAJESTICJUKEBOXRADIO.COM
        </a>
      </footer>

      {/* History Button */}
      <button
        className="history-button"
        onClick={() => setShowHistory(true)}
        data-testid="history-button"
        title="Historique des morceaux"
      >
        <History size={22} />
        {historyCount > 0 && (
          <span className="history-badge">
            {historyCount > 9 ? '9+' : historyCount}
          </span>
        )}
      </button>

      {/* History Panel */}
      <TrackHistory
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />

      {/* Install PWA Button */}
      <InstallPWA />
    </div>
  );
};

export default RadioPlayer;
